import { Controller } from "@hotwired/stimulus"
import { requestIdleCallback } from "global/javascript/request_idle_callback"
import { nodeIdFromUrl } from "global/javascript/utils"

export default class extends Controller {
  static targets = ["mainMenu", "header", "entry"]
  static values = { branchTitle: String, disabled: Boolean }

  connect() {
    this.visibleEntries = []

    this.#observeIntersectingEntries()
    // This is needed, in case the loading order of the stimulus
    // controller is inverted (in case the doc-navigation controller
    // is loaded BEFORE the embed controller)
    this.#maybeRefreshInterstingEntriesOnEmbedMode()

    this.#setInitialActive()
  }

  #maybeRefreshInterstingEntriesOnEmbedMode() {
    document.addEventListener("embed:bodyTopChange", this.#observeIntersectingEntries.bind(this))
  }

  #observeIntersectingEntries() {
    const headerHeight = this.headerTargets.reduce(
      (accu, target) => Math.max(accu, target.getBoundingClientRect().height),
      0
    ) + 2 // Safe 2px margin extra

    const embedHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--body-top")) || 0
    const totalMargin = headerHeight + embedHeight

    if (this.observer) {
      this.observer.disconnect()
    } else {
      this.observer = new IntersectionObserver(
        this.handleIntersectingEntries.bind(this),
        {
          rootMargin: `-${totalMargin}px 0px 0px 0px`
        }
      )

      this.entryTargets.forEach(entry => { this.observer.observe(entry) })
    }
  }

  handleIntersectingEntries(entries) {
    const self = this
    requestIdleCallback(() => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          self.insertEntry(entry)
        } else {
          self.removeEntry(entry)
        }
      })

      if (this.visibleEntries.length) {
        this.setActive(this.visibleEntries[0])
        self.element.setAttribute("data-active-container", this.visibleEntries[0].getAttribute("id"))
      }
    })
  }

  #setInitialActive() {
    const activeItems = this.mainMenuTarget.querySelectorAll("[class~=\"active\"]")

    activeItems.forEach((item) => {
      // When 2 active items, there's a parent & child section
      if (activeItems.length === 2 && !item.getAttribute("data-menu-parent")) {
        this.activeParent = item
      } else {
        this.activeItem = item
      }
    })
  }

  setActive(target) {
    if (this.disabled) return

    this.activeItem?.classList.remove("active")
    this.activeParent?.classList.remove("active")

    const id = target.getAttribute("id") || target.dataset.sectionId

    this.activeItem = this.mainMenuTarget.querySelector(`[data-section-id="${id}"]`)

    if (this.activeItem) {
      const current = this.element.querySelector("[aria-current='true']")
      current?.setAttribute("aria-current", false)
      target.setAttribute("aria-current", true)
      const parentId = this.activeItem.getAttribute("data-menu-parent")

      if (parentId) {
        // the *= selector is to get an element containing given string (contains the path with the parent ID followed by query params '?')
        // the $= selector is to get an element ending with given string (ends with a hash or with a path, containing the parent ID)
        this.activeParent = this.mainMenuTarget.querySelector(`[href$="/${parentId}"], [href*="/${parentId}?"]`) || this.mainMenuTarget.querySelector(`[href$="#${parentId}"]`)

        if (this.activeParent) {
          this.activeParent.classList.add("active")
        }
      } else {
        this.activeParent = null
      }

      const href = new URL(window.location.search, this.activeItem.href)
      this.#updateDocumentTitle()
      history.replaceState({}, "", href.toString())
      this.activeItem.classList.add("active")
      this.#scrollCenterItemInNav(this.activeItem)
    }
  }

  #scrollCenterItemInNav() {
    const navDrawer = this.activeItem.closest(".nav-drawer")
    const navDrawerHalfHeight = navDrawer.getBoundingClientRect().height / 2

    // Move scroll position of the nav drawer to center the active item
    navDrawer.scrollTop = this.activeItem.offsetTop - navDrawerHalfHeight
  }

  insertEntry(entry) {
    const top = entry.target.getBoundingClientRect().top

    if (this.visibleEntries[0]?.getBoundingClientRect().top > top) {
      // We are scrolling up: we insert the target at the beginning of the Array
      this.visibleEntries.unshift(entry.target)
    } else {
      // Scroll down: push at the end of the Array
      this.visibleEntries.push(entry.target)
    }
  }

  removeEntry(entry) {
    const index = this.visibleEntries.indexOf(entry.target)
    if (index > -1) {
      this.visibleEntries.splice(index, 1)
    }
  }

  back(event) {
    const targetUrl = new URL(event.target.location)
    const id = nodeIdFromUrl(targetUrl)

    this.#visitNode(id)
  }

  get disabled() {
    if (this.disabledValue) {
      // Refresh disabled status depending on the presence of the CSS class
      this.disabledValue = this.element.classList.contains("no-single-page-xp")
    }
    return this.disabledValue
  }

  click(event) {
    const clickedSection = event.currentTarget
    const targetUrl = new URL(clickedSection.href)
    const sectionId = clickedSection.dataset.sectionId || nodeIdFromUrl(targetUrl, true)
    let backUrl = undefined

    // Open or close collapsible section
    if (this.#isCollapsibleSection(clickedSection)) {
      if (clickedSection === this.activeItem || clickedSection === this.activeParent) {
        event.preventDefault()
        this.#unsetActive()
        return
      } else {
        this.setActive(clickedSection)
      }
    }

    if (this.disabled) return
    event.preventDefault()

    // Merge query params from current URL & target URL
    targetUrl.search = new URLSearchParams({
      ...Object.fromEntries(new URLSearchParams(window.location.search)),
      ...Object.fromEntries(targetUrl.searchParams)
    }).toString()

    // Event params
    const {storeBackLink, highlight} = event.params
    if (storeBackLink) {
      backUrl = window.location.toString()
    }

    this.#visitNode(sectionId, targetUrl, backUrl, highlight)
  }

  #visitNode(id, targetUrl = undefined, backUrl = undefined, highlight = undefined) {
    const sectionNode = document.getElementById(id)
    const hash = targetUrl && targetUrl.hash.slice(1)
    // Select target node from target URL Fragment (if it's available)
    // otherwise use the section node
    const targetNode = hash && document.getElementById(hash) || sectionNode

    if (!sectionNode) return

    // Set target URL in history if it's given as input
    if (targetUrl) {
      window.history.pushState({}, "", targetUrl.toString())
    }

    // Remember 'back URL' for doc-back-link enabled items
    this.dispatch("doc-back-link:remember", { prefix: false, target: sectionNode, detail: { backUrl } })

    if (sectionNode.tagName === "TURBO-FRAME" && sectionNode.getAttribute("complete") === null) {
      // IF When active item is a NON-LOADED turbo-frame
      this.dispatch(
        "navigateToFrame",
        { target: document, prefix: false, detail: { section: sectionNode, attributeId: hash, highlight } }
      )
      sectionNode.removeAttribute("loading")
      sectionNode.reload()
    } else {
      // ELSE When active item is a LOADED turbo-frame
      //
      // Update back link for doc-back-link loaded active items
      //
      // Note: Non loaded frames will only update the back-link when
      // the frame is rendered (see doc-frame#afterRender).
      this.dispatch("doc-back-link:update", { prefix: false, target: sectionNode })
      // Optionally highlight the target node
      if (highlight) this.dispatch(
        "highlight",
        { target: document, detail: { element: targetNode }, prefix: false }
      )
    }

    // Finally, “go to” the target node
    this.dispatch(
      "goTo",
      { target: document, detail: { element: targetNode }, prefix: false }
    )
  }

  #updateDocumentTitle() {
    let subtitle = undefined
    if (this.activeItem.dataset.metaTitle !== undefined) {
      subtitle = this.activeItem.dataset.metaTitle
    } else {
      subtitle = this.activeItem.firstChild.textContent.trim()
    }

    if (subtitle) {
      document.title = `${subtitle} | ${this.branchTitleValue}`
    } else {
      document.title = this.branchTitleValue
    }
  }

  #isCollapsibleSection(target) {
    return !!target.dataset.sectionCollapsible
  }

  #unsetActive() {
    this.activeItem?.classList.remove("active")
    this.activeItem = null
    this.activeParent?.classList.remove("active")
    this.activeParent = null
  }
}
